/**
 * Duplicar una campaña y borrar un borrador (27/10/2026). Las dos son del equipo de Deenex, como armar una campaña: el
 * backend se las niega a una persona de un cliente (403 SOLO_EQUIPO), así que a ella no se le muestran.
 *
 * - «Duplicar» arma en el backend un borrador nuevo con la configuración de la campaña (plantilla, audiencia, variables,
 *   horario y baja; nada de lo que salió) y abre el asistente en él. El asistente lo retoma en el paso que corresponde
 *   y vuelve a mirar todo: una plantilla pausada o rechazada, una audiencia vacía, el horario. Con el horario y la baja
 *   de la original llega a «Revisar y mandar», donde se elige cuándo sale y se prueba de nuevo (29/10/2026). Lo mismo
 *   hace «Empezar de una anterior» de Nueva campaña (`useDuplicarCampana`).
 * - «Borrar borrador» pide confirmación en la misma pantalla, sin ventana encima (Alan): primero el botón, después
 *   «¿Borrar …?» con «Sí, borrar» y «No, dejarlo». Sólo se borra un borrador que nunca mandó nada; si no, el backend
 *   responde 409 con el porqué (una campaña que mandó se cancela) y se muestra ese texto.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Loader2, Trash2 } from "lucide-react";
import { oficialApi } from "../api";
import type { CampanaDetalle } from "../types";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";

const B = "/activa/oficial/campanas";

/** Dónde se retoma un borrador: el asistente, sin `?paso=` (calcula solo hasta dónde se puede llegar). */
export const rutaAsistenteCampana = (id: string) => `${B}/${encodeURIComponent(id)}/editar`;

/** «enlace»: texto chico, para la fila de la lista. «boton»: con borde, para el encabezado del detalle y del asistente. */
type Estilo = "enlace" | "boton";

const CLASE: Record<Estilo, { normal: string; peligro: string }> = {
  enlace: {
    normal: "inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:underline disabled:opacity-60",
    peligro: "inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-700 disabled:opacity-60",
  },
  boton: {
    normal: "h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50",
    peligro: "h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-red-200 hover:text-red-700 inline-flex items-center gap-1.5 disabled:opacity-50",
  },
};

/**
 * Duplicar una campaña y abrir la copia en el asistente, sin `?paso=`: lo guardado dice hasta dónde llega. `duplicando` y
 * `error` dicen de cuál (una lista ofrece varias). El backend se lo niega a quien no es del equipo (403 SOLO_EQUIPO).
 */
export function useDuplicarCampana() {
  const navigate = useNavigate();
  const [duplicando, setDuplicando] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; texto: string } | null>(null);
  const duplicar = async (id: string) => {
    if (duplicando) return;
    setDuplicando(id);
    setError(null);
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.duplicarCampana(id));
      if (!l.ok || !l.data?.id) {
        setError({ id, texto: l.ok ? "El servidor no devolvió la copia." : textoError(l) });
        return;
      }
      navigate(rutaAsistenteCampana(l.data.id));
    } catch (e) {
      setError({ id, texto: textoError(leerError(e)) });
    } finally {
      setDuplicando(null);
    }
  };
  return { duplicar, duplicando, error };
}

export function BotonDuplicarCampana({ id, estilo = "boton" }: { id: string; estilo?: Estilo }) {
  const equipo = useEsEquipoDeenex();
  const { duplicar, duplicando, error } = useDuplicarCampana();
  if (!equipo) return null;

  return (
    <span className="inline-flex flex-col gap-1">
      <button type="button" onClick={() => void duplicar(id)} disabled={!!duplicando} title="Arma un borrador nuevo con la misma plantilla, audiencia, variables, horario y baja."
        className={CLASE[estilo].normal}>
        {duplicando ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} {duplicando ? "Duplicando…" : "Duplicar"}
      </button>
      {error && <span role="alert" className="text-xs text-red-700">{error.texto}</span>}
    </span>
  );
}

export function BorrarBorrador({ id, nombre, onBorrada, estilo = "boton" }: { id: string; nombre: string; onBorrada: () => void; estilo?: Estilo }) {
  const equipo = useEsEquipoDeenex();
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState("");
  if (!equipo) return null;

  const borrar = async () => {
    if (borrando) return;
    setBorrando(true);
    setError("");
    try {
      const l = leerRespuesta<{ borrada: true }>(await oficialApi.borrarCampana(id));
      if (!l.ok) {
        setError(textoError(l));
        setConfirmando(false);
        return;
      }
      onBorrada();
    } catch (e) {
      // 409 CAMPANA_NO_BORRABLE: el texto del backend dice por qué (ya se confirmó, ya tiene mensajes) y qué hacer
      setError(textoError(leerError(e)));
      setConfirmando(false);
    } finally {
      setBorrando(false);
    }
  };

  return (
    <span className="inline-flex flex-col gap-1">
      {!confirmando ? (
        <button type="button" onClick={() => { setError(""); setConfirmando(true); }} className={CLASE[estilo].peligro}>
          <Trash2 size={13} /> Borrar borrador
        </button>
      ) : (
        <span role="group" aria-label="Confirmar el borrado del borrador" className="inline-flex items-center gap-2 flex-wrap text-xs">
          <span className="text-slate-600">¿Borrar «{nombre}»? No se puede deshacer.</span>
          <button type="button" onClick={() => void borrar()} disabled={borrando} className="font-semibold text-red-700 hover:underline disabled:opacity-60">
            {borrando ? "Borrando…" : "Sí, borrar"}
          </button>
          <button type="button" onClick={() => setConfirmando(false)} disabled={borrando} className="font-semibold text-slate-500 hover:underline disabled:opacity-60">
            No, dejarlo
          </button>
        </span>
      )}
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
    </span>
  );
}
